/**
 * Pure, dependency-free assistant lookups over the redux state shape.
 *
 * This lives in its own module rather than inside `assistantsHelper.js` so that lightweight
 * presentation helpers can resolve an assistant without inheriting that module's transitive
 * imports: `assistantsHelper` reaches `utils/backends/firestore.js`, which requires build-time
 * dotenv variables and therefore cannot be imported from store-only helpers or their unit
 * tests. `assistantsHelper` re-exports `getAssistantFromState` so there is still exactly one
 * implementation.
 */

/**
 * Find an assistant by id anywhere the client can see it: the shared global pool first, then
 * every project the logged user has loaded.
 *
 * Assistants are stored per project (`assistants/{projectId}/items`) plus one shared
 * `assistants/globalProject/items` pool. Project collections load on demand, so this lookup
 * only sees profiles already loaded. Author presentation separately probes accessible projects
 * on a miss and subscribes to state changes when profiles arrive. Cross-project authors are valid:
 * objects legitimately keep an assistant from the user's default project while living in a
 * different project (see `getAssignedAssistantInProjectContext` in `assistantsHelper.js`).
 *
 * Returns null when the id belongs to no visible assistant (a human, a contact, a workstream,
 * or an assistant in a project this user is not a member of).
 */
export const getAssistantFromState = (state, assistantId) => {
    if (!assistantId) return null

    const globalAssistant = (state?.globalAssistants || []).find(assistant => assistant.uid === assistantId)
    if (globalAssistant) return globalAssistant

    const projectAssistants = state?.projectAssistants || {}
    for (const assistants of Object.values(projectAssistants)) {
        const assistant = (assistants || []).find(item => item.uid === assistantId)
        if (assistant) return assistant
    }

    return null
}

export const getAssistantAuthorProjectIds = state => {
    const ids = new Set(Object.keys(state.loggedUserProjectsMap || {}))
    // User membership can arrive before project documents; archived projects are not necessarily
    // mounted in the sidebar either. Firestore still checks access on every targeted probe.
    for (const field of ['projectIds', 'guideProjectIds', 'templateProjectIds', 'archivedProjectIds']) {
        for (const id of state.loggedUser?.[field] || []) {
            if (typeof id === 'string' && id.trim()) ids.add(id.trim())
        }
    }
    return [...ids].sort()
}
